import { Fragment, useCallback, useEffect, useRef } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { useRouter } from "next/router";
import {
  filterSelectedAttributeIdsAtom,
  filterSelectedAttributeIdsSelector,
  searchFormSelector,
  sortOrderAtom,
  minPriceAtom,
  maxPriceAtom,
} from "../../../state/search-state";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { BannersSearch } from "../../components/property/BannersSearch";
import { PropertyCard } from "../../components/property/PropertyCard";
import { useInfiniteListFutureOffers, useInfiniteListOffers } from "../../../api/hooks";
import { ReadMore as ReadMoreIcon } from "@mui/icons-material";
import { Props } from "./Props";
import { makeUrl } from "../../../fe-ui/utils/makeUrl";
import { EmptyContent } from "../../../fe-ui/blocks/EmptyContent/EmptyContent";
import { SkeletonUnitsList } from "../../components/SkeletonUnitsList/SkeletonUnitsList";
import { adultsCounterAtom, childrenCounterAtom } from "../../../state/booking-state";
import { useTranslation } from "../../../fe-ui/hooks/use-translation";
import { useEmbeddedContext } from "../../EmbeddedContext";

const bannerSizeBy: Record<"one" | "two" | "four", number> = {
  one: 1,
  two: 2,
  four: 4,
};

const LoadingState = (counter) => (
  <Box display={"flex"} justifyContent={"center"} my={2}>
    <SkeletonUnitsList counter={counter} />
  </Box>
);

const ErrorState = () => (
  <Box display={"flex"} justifyContent={"center"} my={2}>
    <Button variant={"outlined"} color={"error"}>
      {"Error"}
    </Button>
  </Box>
);

const EnsureDatesEnabled = ({ children }) => {
  const filtersValue = useRecoilValue(searchFormSelector);
  const { dateRange } = filtersValue;
  const [arrivalDate, departureDate] = dateRange;
  if (arrivalDate && departureDate) {
    return children;
  }
  return null;
};

const OffersList = (props: Props) => {
  const {
    bannersSearch,
    bannerBlockId,
    perPage = 4,
    withAttributesContext,
    withGuestsContext,
    infinite = true,
    cityIdContext: cityId,
    hotelIdContext: hotelId,
    type,
    initialPage,
    guestsCount = 2,
    showTotalPrice = false,
    dataTestId,
    disableOpenInNewTab,
    forceAttributes,
  } = props;
  const selectedAttributes = useRecoilValue(filterSelectedAttributeIdsSelector);
  const sortOrder = useRecoilValue(sortOrderAtom);
  const minPrice = useRecoilValue(minPriceAtom);
  const maxPrice = useRecoilValue(maxPriceAtom);
  const totalAdults = useRecoilValue(adultsCounterAtom);
  const totalChildren = useRecoilValue(childrenCounterAtom);
  const filtersValue = useRecoilValue(searchFormSelector);
  const { dateRange } = filtersValue;
  const [arrivalDate, departureDate] = dateRange;
  //
  const { asPath } = useRouter();
  const t = useTranslation();
  const resetAttributes = useResetRecoilState(filterSelectedAttributeIdsAtom);

  const pageSearchBanners = bannersSearch?.filter((banner) => banner.attributes?.blockId === bannerBlockId);
  const firstPageBanner = pageSearchBanners?.find((banner) => banner.attributes?.page === 1);
  const currentPerPage = useRef<number>(
    firstPageBanner ? perPage - bannerSizeBy[firstPageBanner.attributes?.size || "one"] : perPage
  );

  const isFuture = type === "future";

  const useList = isFuture ? useInfiniteListFutureOffers : useInfiniteListOffers;

  const { basePath } = useEmbeddedContext();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useList({
    currentPerPage,
    pagePath: asPath,
    arrivalDate: isFuture ? "" : `${arrivalDate?.format("YYYY-MM-DD")}`,
    departureDate: isFuture ? "" : `${departureDate?.format("YYYY-MM-DD")}`,
    cities: cityId ? cityId.toString() : undefined,
    hotelIds: hotelId ? hotelId : undefined,
    attributeIds: forceAttributes
      ? forceAttributes.join(",")
      : withAttributesContext && selectedAttributes.length > 0
      ? selectedAttributes.join(",")
      : undefined,
    adults: withGuestsContext ? totalAdults : guestsCount, // 2 guests by default
    children: withGuestsContext ? totalChildren : 0, // 0 children by default
    sortOrder,
    minPrice,
    maxPrice,
    perPage,
  });

  useEffect(() => {
    if (firstPageBanner) {
      currentPerPage.current = perPage - bannerSizeBy[firstPageBanner.attributes?.size || "one"];
    } else {
      currentPerPage.current = perPage;
    }
  }, [firstPageBanner, perPage]);

  const handleLoadNext = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const currentPage = initialPage === 1 ? data?.pages.length : (data?.pages.length ?? 0) + 1;
      const foundBanner = pageSearchBanners?.find((banner) => banner.attributes?.page === currentPage);
      if (foundBanner) {
        currentPerPage.current = perPage - bannerSizeBy[foundBanner.attributes?.size || "one"];
      } else {
        currentPerPage.current = perPage;
      }
      void fetchNextPage();
    },
    [data?.pages.length, fetchNextPage, initialPage, pageSearchBanners, perPage]
  );

  useEffect(() => {
    if (data && data.pages.length === 1 && initialPage === 1) {
      void fetchNextPage();
    }
  }, [initialPage, fetchNextPage, data]);

  const loadNextIsDisabled = !hasNextPage || isFetchingNextPage;

  if (status === "loading") {
    return <LoadingState counter={perPage} />;
  }

  if (status === "error") {
    return <ErrorState />;
  }

  if (data === undefined || data?.pages?.[0].length === 0) {
    return (
      <EmptyContent
        filled
        title={t("search-nothing-found-title")}
        description={
          <>
            {t.rich("search-nothing-found-try-different-search", {
              br: () => <br />,
              clearfilter: (x) => (
                <Button onClick={resetAttributes} size="small" variant="text">
                  {x}
                </Button>
              ),
              changedates: () => <a />,
            })}
          </>
        }
      />
    );
  }

  return (
    <>
      <Grid container spacing={4} data-testid={dataTestId}>
        {data.pages
          .filter((group, i) => infinite || i === 0)
          .map((group, i) =>
            !initialPage || i >= initialPage ? (
              <Fragment key={i}>
                {group.map((offer, offerIdx) => {
                  const [cheapestRatePlan] = offer.ratePlans.sort((a, b) => {
                    return a.pricePerNightDetails.priceWithTax - b.pricePerNightDetails.priceWithTax;
                  });
                  const imageUrlList = offer.unitGroup.media.reduce((acc: string[], media, idx, origArr) => {
                    if (acc.length > 9) {
                      origArr.splice(idx);
                      return acc;
                    }
                    return media?.media_type === "photo" ? [...acc, media?.url] : acc;
                  }, []);

                  return (
                    <Fragment key={offer.unitGroupId}>
                      {pageSearchBanners?.length ? (
                        <BannersSearch
                          banners={pageSearchBanners}
                          currentPage={initialPage === 1 ? i : i + 1}
                          position={offerIdx}
                        />
                      ) : null}
                      <Grid item xs={12} sm={6} md={4} lg={3}>
                        {/* TODO: Вынести в отдельный компонент */}
                        <PropertyCard
                          area={offer.unitGroup.area}
                          attributes={offer.unitGroup.attributes}
                          showTotalPrice={showTotalPrice}
                          link={makeUrl(`${basePath ? basePath : ""}/unit/${offer.unitGroupId}`, {
                            arrivalDate: offer?.arrivalDate || arrivalDate?.format("YYYY-MM-DD"),
                            departureDate: offer?.departureDate || departureDate?.format("YYYY-MM-DD"),
                            adults: totalAdults,
                            children: totalChildren,
                            hotelId: offer.hotelId,
                            unitGroupId: offer.unitGroupId,
                            ratePlanId: cheapestRatePlan.ratePlanId,
                          })}
                          promotionLabels={offer.unitGroup.promotionLabels}
                          title={offer.unitGroup.title}
                          description={offer.unitGroup.shortDescription || offer.unitGroup.description}
                          pricePerNightDetails={cheapestRatePlan.pricePerNightDetails}
                          image={offer.unitGroup.media[0]?.url}
                          imageList={imageUrlList}
                          arrivalDate={offer?.arrivalDate}
                          departureDate={offer?.departureDate}
                          id={offer.unitGroupId}
                          subtitle={offer.hotel?.title}
                          hotelId={offer.hotelId}
                          priceDetails={cheapestRatePlan.priceDetails}
                          openInNewTab={!disableOpenInNewTab}
                        />
                      </Grid>
                    </Fragment>
                  );
                })}
              </Fragment>
            ) : null
          )}
      </Grid>
      {infinite && (
        <>
          {/* TODO: LoadNextButton Component */}
          <Box display={"flex"} justifyContent={"center"} my={2}>
            {hasNextPage && (
              <Button
                startIcon={!isFetchingNextPage && <ReadMoreIcon />}
                data-testid="load-more-button"
                data-ym={"LOAD_MORE"}
                color={"primary"}
                sx={{ borderRadius: 3 }}
                variant={"outlined"}
                onClick={handleLoadNext}
                disabled={loadNextIsDisabled}
              >
                {isFetchingNextPage ? (
                  <>
                    &nbsp;
                    <CircularProgress color={"inherit"} size={20} />
                    &nbsp;
                  </>
                ) : hasNextPage ? (
                  t("cta-load-more")
                ) : (
                  t("cta-load-more-ended")
                )}
              </Button>
            )}
            {!hasNextPage && <>{/* TODO: Final state */}</>}
          </Box>
        </>
      )}
    </>
  );
};

export const OffersListWidget = (props: Props) => {
  const { type } = props;
  if (type === "future") {
    return <OffersList {...props} />;
  }
  return (
    <EnsureDatesEnabled>
      <OffersList {...props} />
    </EnsureDatesEnabled>
  );
};
